import React, { useCallback, useMemo, useState } from "react";

import { Box, Grid } from "@mui/material";
import { Hidden } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import FieldView from "../../../../../../../../components/Form/FieldView";
import AddressComponent from "../../../../../../../../components/Generic/AddressComponent";
import GeneralCommentsComponent from "../../../../../../../../components/Generic/GeneralComments";
import { useAppState } from "../../../../../../../../context/StateContext";
import { Branch } from "../../../../../../../../models/branch";
import { CustomerLocations, Sales } from "../../../../../../../../models/sales";
import BranchService from "../../../../../../../../services/branch.service";
import { handleGetConvertValue } from "../../../../../../../../util/converts";
import { Status } from "../../../../../../../../util/enums";
import { getMeasurement } from "../../../../../../../../util/getMeasurement";
import { getTotalItemsValuesByInventory } from "../../../../../../../../util/inventory";
import useStyles, { GridAddressComponent, Wrapper } from "./styles";

interface Props {
  quote?: Sales;
  onCustomerView?: boolean;
}

export default function FormOnViewMode(props: Props) {
  const { quote, onCustomerView = false } = props;
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const classes = useStyles();

  const [branches, setBranches] = useState<Branch[]>(quote?.branchList ?? []);

  const { code: measurementCode, decimalPlaces } = getMeasurement();

  useQuery(
    ["branches.list"],
    () =>
      BranchService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "ASC"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!quote?.id && !onCustomerView,
      onSuccess: (response) => {
        if (response && response.data && response.data?.length > 0) setBranches(response.data);
      },
    }
  );

  const consultantName = useMemo(() => {
    if (quote?.user) {
      const name = quote?.user?.name;
      const lastName = quote?.user?.lastName;

      return `${name ?? ""} ${lastName ?? ""}`;
    }

    return "";
  }, [quote?.user]);

  const managerName = useMemo(() => {
    if (quote?.manager) {
      const name = quote?.manager?.name;
      const lastName = quote?.manager?.lastName;

      return `${name ?? ""} ${lastName ?? ""}`;
    }

    return "";
  }, [quote?.manager]);

  const inventoryQuotedField = () => {
    return `${
      quote?.customQuantityQuoted === null || quote?.customQuantityQuoted === undefined
        ? getInventoryData().quantity
        : quote?.customQuantityQuoted
    } item(s) - ${
      quote?.customVolumeQuoted === null || quote?.customVolumeQuoted === undefined
        ? getInventoryData().volumeFormatted
        : handleGetConvertValue(
            quote?.customVolumeQuoted || 0,
            measurementCode,
            decimalPlaces
          ).toString()
    } ${measurementCode}`;
  };

  const branchName = useCallback(
    (id?: number) => {
      if (!id) return undefined;

      if (branches.length) {
        const branch = branches?.find((bran) => Number(bran.id) === Number(id));

        if (branch) {
          const { name } = branch;

          return name;
        }
      }

      return "";
    },
    [quote, branches]
  );

  const getInventoryData = useCallback(() => {
    return getTotalItemsValuesByInventory(quote?.inventory, getMeasurement());
  }, [quote, quote?.inventory]);

  const renderOnCustomerViewContent = () => {
    if (onCustomerView) {
      return (
        <>
          <Grid container direction="row" alignItems="center" spacing={2}>
            <Grid item xs={6} sm={6} md={6} lg={3}>
              <FieldView label={translate("quoteType")} value={quote?.jobType} />
            </Grid>
            <Grid item xs={6} sm={6} md={6} lg={3}>
              <FieldView label={translate("reference")} value={quote?.reference} />
            </Grid>
            <Grid item xs={6} sm={6} md={6} lg={3}>
              <FieldView label={translate("status")} value={quote?.jobSubStatus} />
            </Grid>
            <Grid item xs={6} sm={6} lg={3}>
              <FieldView label={translate("inventoryQuoted")} value={inventoryQuotedField()} />
            </Grid>
            <Grid item xs={6} sm={6} lg={3}>
              <FieldView label={translate("mode")} value={quote?.mode ? translate(quote.mode.toLowerCase()) : ""} />
            </Grid>
            <Grid item xs={6} sm={6} md={6} lg={3}>
              <FieldView label={translate("consultant")} value={consultantName} />
            </Grid>
          </Grid>
        </>
      );
    }

    return null;
  };

  return (
    <Wrapper>
      {onCustomerView ? (
        renderOnCustomerViewContent()
      ) : (
        <>
          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Grid item xs={12} sm={6} md={6} lg={2}>
              <FieldView label={translate("quoteType")} value={quote?.jobTypeName} />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={2}>
              <FieldView label={translate("reference")} value={quote?.reference} />
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={2}>
              <FieldView
                label={translate("branchOrigin")}
                value={branchName(quote?.branchFromId)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={2}>
              <FieldView
                label={translate("branchDestination")}
                value={branchName(quote?.toBranchId)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6} lg={2}>
              <FieldView label={translate("status")} value={quote?.jobSubStatus} />
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={2}
          >
            <Grid item xs={12} sm={6} md={6} lg={2}>
              <FieldView label={translate("consultant")} value={consultantName} />
            </Grid>
            <Grid item xs={12} sm={6} lg={2} className={classes.p08}>
              <FieldView label={translate("code")} value={quote?.user?.usersCompanies?.[0]?.code} />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={2}>
              <FieldView label={translate("manager")} value={managerName} />
            </Grid>
            <Box sx={{ display: { xs: "none", sm: "none", md: "block" } }}>
              <Grid item xs={false} sm={6} md={6} lg={2} />
              <Grid item xs={false} sm={6} md={6} lg={2} />
            </Box>
          </Grid>

          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Grid item xs={12} sm={6} lg={2}>
              <FieldView
                label={translate("leadSourceFrom")}
                value={quote?.leadSourceFrom ? quote?.leadSourceFrom?.toString() : undefined}
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={2}>
              <FieldView label={translate("inventoryQuoted")} value={inventoryQuotedField()} />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <FieldView label={translate("mode")} value={quote?.mode ? translate(quote.mode.toLowerCase()) : ""} />
            </Grid>
            <Box sx={{ display: { xs: "none", sm: "none", md: "block" } }}>
              <Grid item xs={false} sm={6} md={6} lg={2} />
              <Grid item xs={false} sm={6} md={6} lg={2} />
            </Box>
          </Grid>

          {quote?.tag && (
            <Grid container direction="row" alignItems="center" spacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={12}>
                <FieldView
                  label={translate("tags")}
                  value={quote?.tag?.split(",")}
                  valueClass={classes.tag}
                />
              </Grid>
            </Grid>
          )}
        </>
      )}
      <Grid container direction="row" alignItems="center">
        <GridAddressComponent onCustomerView={onCustomerView} item sm={12} md={12} lg={12}>
          <AddressComponent
            isOnViewMode
            isRenderOnViewComponent
            title={translate("pickup")}
            access={quote?.pickupAccess}
            inventory={quote?.inventory || []}
            customerLocations={quote?.origin || ({} as CustomerLocations)}
            onCustomerView={onCustomerView}
          />
        </GridAddressComponent>
        <GridAddressComponent onCustomerView={onCustomerView} item sm={12} md={12} lg={12}>
          <AddressComponent
            isOnViewMode
            isRenderOnViewComponent
            title={translate("delivery")}
            access={quote?.deliveryAccess}
            inventory={quote?.inventory || []}
            customerLocations={quote?.destination || ({} as CustomerLocations)}
            onCustomerView={onCustomerView}
          />
        </GridAddressComponent>
        {quote?.quoteComments?.length ? (
          <Grid item sm={12} md={12} lg={12}>
            <GeneralCommentsComponent comments={quote?.quoteComments} isViewMode />
          </Grid>
        ) : null}
      </Grid>
    </Wrapper>
  );
}
